const FINGERPRINT_STORAGE_KEY = "inversa_fingerprint_id";
const FINGERPRINT_MAX_LENGTH = 128;

function buildFingerprintSeed() {
  const { vendor, renderer } = getWebGLInfo();
  return [
    navigator.userAgent,
    navigator.language,
    (navigator.languages || []).join(","),
    window.screen.width,
    window.screen.height,
    window.screen.colorDepth,
    window.devicePixelRatio,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency,
    navigator.deviceMemory,
    navigator.platform,
    vendor,
    renderer,
    navigator.cookieEnabled,
  ].join("|");
}

/** Deterministic SHA-256 of browser seed (no random). Same traits → same id. */
async function createFingerprintId() {
  const data = new TextEncoder().encode(buildFingerprintSeed());
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  return hashArray.map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

/**
 * Stable per-browser id (max 128 chars).
 * Reuses localStorage cache when present; otherwise computes SHA-256(browserSeed)
 * so the same machine/browser traits recreate the same id after storage clear.
 */
export async function getFingerprintId() {
  try {
    const stored = localStorage.getItem(FINGERPRINT_STORAGE_KEY);
    if (stored && stored.length <= FINGERPRINT_MAX_LENGTH) return stored;
    if (stored) localStorage.removeItem(FINGERPRINT_STORAGE_KEY);
  } catch {
    // localStorage may be unavailable
  }

  const fingerprintId = await createFingerprintId();

  try {
    localStorage.setItem(FINGERPRINT_STORAGE_KEY, fingerprintId);
  } catch {
    // ignore persistence errors
  }

  return fingerprintId;
}

function getWebGLInfo() {
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) return { vendor: "", renderer: "" };

    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
    return {
      vendor: debugInfo
        ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || ""
        : "",
      renderer: debugInfo
        ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || ""
        : "",
    };
  } catch {
    return { vendor: "", renderer: "" };
  }
}

const IP_ISP_PATTERN = /^[a-zA-Z\s0-9.-]+$/;
const UNKNOWN_OS_VERSION = "0.0";

function sanitizeIpIsp(value) {
  const raw = String(value || "").trim();
  if (!raw) return "Unknown";

  if (IP_ISP_PATTERN.test(raw)) return raw;

  const sanitized = raw
    .replace(/[^a-zA-Z\s0-9.-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return sanitized && IP_ISP_PATTERN.test(sanitized) ? sanitized : "Unknown";
}

function parseOsInfo(userAgent) {
  const ua = userAgent || navigator.userAgent;
  let os = "Unknown";
  let os_version = "";

  if (/Windows NT ([\d.]+)/.test(ua)) {
    os = "Windows";
    os_version = ua.match(/Windows NT ([\d.]+)/)?.[1] || "";
  } else if (/Mac OS X ([\d_]+)/.test(ua)) {
    os = "MacOS";
    os_version = (ua.match(/Mac OS X ([\d_]+)/)?.[1] || "").replace(/_/g, ".");
  } else if (/CrOS/.test(ua)) {
    os = "ChromeOS";
    os_version = ua.match(/CrOS [^ ]+ ([\d.]+)/)?.[1] || "";
  } else if (/Android ([\d.]+)/.test(ua)) {
    os = "Android";
    os_version = ua.match(/Android ([\d.]+)/)?.[1] || "";
  } else if (/iPhone OS ([\d_]+)/.test(ua)) {
    os = "iOS";
    os_version = (ua.match(/iPhone OS ([\d_]+)/)?.[1] || "").replace(/_/g, ".");
  } else if (/Linux/.test(ua)) {
    os = "Linux";
    os_version =
      ua.match(/Ubuntu\/([\d.]+)/)?.[1] ||
      ua.match(/Fedora\/([\d.]+)/)?.[1] ||
      "";
  }

  return { os, os_version };
}

async function resolveOsVersion(userAgent) {
  const { os_version: parsedVersion } = parseOsInfo(userAgent);
  if (parsedVersion) return parsedVersion;

  try {
    const uaData = navigator.userAgentData;
    if (uaData?.getHighEntropyValues) {
      const hints = await uaData.getHighEntropyValues(["platformVersion"]);
      if (hints.platformVersion) return hints.platformVersion;
    }
  } catch {
    // Client hints may be blocked or unavailable.
  }

  return UNKNOWN_OS_VERSION;
}

function guessArchitecture(userAgent) {
  const ua = userAgent || navigator.userAgent;
  if (/aarch64|arm64/i.test(ua)) return "arm64";
  if (/x86_64|x64|Win64|WOW64/i.test(ua)) return "x86";
  if (/iPhone|iPad|Android/i.test(ua)) return "arm";
  return "unknown";
}

function guessBitness(userAgent) {
  const ua = userAgent || navigator.userAgent;
  if (/Win64|x64|x86_64|WOW64|aarch64|arm64/i.test(ua)) return "64";
  if (/iPhone|iPad|Android|Mobile/i.test(ua)) return "64";
  return "32";
}

function parseBrowserNameVersion(userAgent) {
  const ua = userAgent || navigator.userAgent;
  let name = "Unknown";
  let version = "";

  if (/Edg\//.test(ua)) {
    name = "Edge";
    version = ua.match(/Edg\/([\d.]+)/)?.[1] || "";
  } else if (/OPR\//.test(ua) || /Opera/.test(ua)) {
    name = "Opera";
    version = ua.match(/(?:OPR|Opera)\/([\d.]+)/)?.[1] || "";
  } else if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) {
    name = "Chrome";
    version = ua.match(/Chrome\/([\d.]+)/)?.[1] || "";
  } else if (/Firefox\//.test(ua)) {
    name = "Firefox";
    version = ua.match(/Firefox\/([\d.]+)/)?.[1] || "";
  } else if (/Safari\//.test(ua) && /Version\//.test(ua)) {
    name = "Safari";
    version = ua.match(/Version\/([\d.]+)/)?.[1] || "";
  }

  return { name, version };
}

async function hashString(value) {
  const data = new TextEncoder().encode(value);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  return hashArray.map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function getCanvasHash() {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 280;
    canvas.height = 60;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";

    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#f60";
    ctx.fillRect(100, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.font = '11pt "Times New Roman"';
    ctx.fillText("Cwm fjordbank glyphs vext quiz, 😃", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.font = "18pt Arial";
    ctx.fillText("Cwm fjordbank glyphs vext quiz, 😃", 4, 45);

    return hashString(canvas.toDataURL());
  } catch {
    return "";
  }
}

/** Browser section of the register API `client` object (filled from navigator). */
export async function getBrowserClientInfo() {
  const userAgent = navigator.userAgent;
  const { name, version } = parseBrowserNameVersion(userAgent);

  return {
    user_agent: userAgent,
    name,
    version,
    cookie_enabled: navigator.cookieEnabled,
    do_not_track: navigator.doNotTrack ?? null,
    canvas_hash: await getCanvasHash(),
    webgl: getWebGLInfo(),
  };
}

function getScreenClientInfo() {
  return {
    w: window.screen.width,
    h: window.screen.height,
    dpr: window.devicePixelRatio || 1,
  };
}

async function getDeviceClientInfo() {
  const userAgent = navigator.userAgent;
  const { os } = parseOsInfo(userAgent);
  const uaData = navigator.userAgentData;
  const os_version = await resolveOsVersion(userAgent);

  return {
    platform: uaData?.platform || navigator.platform || "",
    os,
    os_version,
    hardware_concurrency: Math.max(navigator.hardwareConcurrency ?? 1, 1),
    device_memory: Math.max(navigator.deviceMemory ?? 1, 1),
    max_touch_points: navigator.maxTouchPoints ?? 0,
    architecture: guessArchitecture(userAgent),
    bitness: guessBitness(userAgent),
  };
}

function getTimezoneClientInfo() {
  return {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    timezone_offset: new Date().getTimezoneOffset(),
  };
}

function getLanguagesClientInfo() {
  const languages = navigator.languages?.length
    ? [...navigator.languages]
    : navigator.language
      ? [navigator.language]
      : ["en"];

  return { languages };
}

const EMPTY_SERVER_INFO = {
  ip: "",
  ip_country: "",
  ip_city: "",
  ip_region: "",
  ip_asn: "",
  ip_isp: "Unknown",
};

const SERVER_INFO_STORAGE_KEY = "inversa_ip_geo";
const SERVER_INFO_TTL_MS = 15 * 60 * 1000;
const GEO_LOOKUP_TIMEOUT_MS = 5000;

function formatAsn(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (/^AS\d+$/i.test(raw)) return raw.toUpperCase();

  const digits = raw.replace(/\D/g, "");
  return digits ? `AS${digits}` : "";
}

/**
 * Geo providers tried in order. Each is free, HTTPS and CORS-enabled; the list
 * exists because a single provider rate limits (ipapi.co answers 429 once its
 * free quota for the caller's IP is spent) and an empty ip_country fails
 * register validation.
 */
const GEO_PROVIDERS = [
  {
    url: "https://ipwho.is/",
    parse: (data) => ({
      ip: data.ip || "",
      ip_country: data.country_code || "",
      ip_city: data.city || "",
      ip_region: data.region || "",
      ip_asn: formatAsn(data.connection?.asn),
      ip_isp: sanitizeIpIsp(data.connection?.isp || data.connection?.org),
    }),
  },
  {
    url: "https://ipapi.co/json/",
    parse: (data) => ({
      ip: data.ip || "",
      ip_country: data.country_code || data.country || "",
      ip_city: data.city || "",
      ip_region: data.region || "",
      ip_asn: formatAsn(data.asn),
      ip_isp: sanitizeIpIsp(data.org),
    }),
  },
  {
    url: "https://get.geojs.io/v1/ip/geo.json",
    parse: (data) => ({
      ip: data.ip || "",
      ip_country: data.country_code || "",
      ip_city: data.city || "",
      ip_region: data.region || "",
      ip_asn: formatAsn(data.asn),
      ip_isp: sanitizeIpIsp(data.organization_name || data.organization),
    }),
  },
  {
    // Country only — last resort, since ip_country is the validated field.
    url: "https://api.country.is/",
    parse: (data) => ({
      ip: data.ip || "",
      ip_country: data.country || "",
    }),
  },
];

let serverInfoCache = null;
let serverInfoRequest = null;

function readCachedServerInfo() {
  if (serverInfoCache && serverInfoCache.expiresAt > Date.now()) {
    return serverInfoCache.value;
  }
  serverInfoCache = null;

  try {
    const stored = JSON.parse(
      localStorage.getItem(SERVER_INFO_STORAGE_KEY) || "null"
    );
    if (stored?.value?.ip_country && stored.expiresAt > Date.now()) {
      serverInfoCache = stored;
      return stored.value;
    }
    if (stored) localStorage.removeItem(SERVER_INFO_STORAGE_KEY);
  } catch {
    // localStorage may be unavailable or hold malformed JSON
  }

  return null;
}

function cacheServerInfo(value) {
  const entry = { value, expiresAt: Date.now() + SERVER_INFO_TTL_MS };
  serverInfoCache = entry;

  try {
    localStorage.setItem(SERVER_INFO_STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // ignore persistence errors
  }
}

async function fetchGeoJson(url) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), GEO_LOOKUP_TIMEOUT_MS);

  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) return null;

    const data = await response.json();
    // Providers also report quota and lookup failures in a 200 body.
    if (!data || data.error || data.success === false) return null;

    return data;
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

async function resolveServerClientInfo() {
  for (const provider of GEO_PROVIDERS) {
    const data = await fetchGeoJson(provider.url);
    if (!data) continue;

    const info = { ...EMPTY_SERVER_INFO, ...provider.parse(data) };
    if (!info.ip_country) continue;

    cacheServerInfo(info);
    return info;
  }

  // Nothing resolved: send the bare IP and stay uncached so the next attempt
  // retries instead of repeating a countryless payload for the whole TTL.
  const ipData = await fetchGeoJson("https://api.ipify.org?format=json");
  return { ...EMPTY_SERVER_INFO, ip: ipData?.ip || "" };
}

/**
 * IP / geo for register `client.server` (resolved from the user's network).
 * Cached for {@link SERVER_INFO_TTL_MS} and de-duplicated while in flight, so
 * repeated register/login attempts reuse one lookup rather than spending the
 * provider quota and falling back to an empty ip_country.
 */
export async function fetchServerClientInfo() {
  const cached = readCachedServerInfo();
  if (cached) return { ...cached };

  if (!serverInfoRequest) {
    serverInfoRequest = resolveServerClientInfo().finally(() => {
      serverInfoRequest = null;
    });
  }

  return { ...(await serverInfoRequest) };
}

function getConnectionClientInfo() {
  const connection =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection;

  if (!connection) {
    return {
      type: "unknown",
      effective_type: "unknown",
      downlink: 0,
      rtt: 0,
    };
  }

  return {
    type: connection.type || "unknown",
    effective_type: connection.effectiveType || "unknown",
    downlink: connection.downlink ?? 0,
    rtt: connection.rtt ?? 0,
  };
}

/** Top-level `client` object for the register API. */
export async function getRegisterClientInfo() {
  const [server, fingerprintId, browser, device] = await Promise.all([
    fetchServerClientInfo(),
    getFingerprintId(),
    getBrowserClientInfo(),
    getDeviceClientInfo(),
  ]);

  return {
    auth_method: "password",
    fingerprint_id: fingerprintId,
    screen: getScreenClientInfo(),
    device,
    ...getTimezoneClientInfo(),
    ...getLanguagesClientInfo(),
    server,
    browser,
  };
}

/** `client` object for player MPIN API (includes connection). */
export async function getMpinClientInfo() {
  return {
    ...(await getRegisterClientInfo()),
    connection: getConnectionClientInfo(),
  };
}
